import React from "react";
import { motion } from "framer-motion";
import {
  Droplets,
  ShieldCheck,
  Home,
  Building2,
  Layers,
  Wrench,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const WhatIsWaterproofing = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="bg-[#f7f7f7] py-32">
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="max-w-6xl mx-auto px-6 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            What is Waterproofing?
          </h1>
          <p className="text-gray-600 text-xl max-w-3xl mx-auto">
           Complete protection for your home against leakage, dampness, and long-term structural damage through professional waterproofing systems that enhance safety, durability, and indoor comfort.
          </p>
        </motion.div>
      </section>

      {/* SECTION 1 */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show">
            <h2 className="text-3xl font-semibold mb-4">
              Understanding Waterproofing
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Waterproofing is a professional treatment process that protects a
              building from water penetration by creating a durable protective
              barrier. It prevents moisture, rainwater, and humidity from
              entering critical parts of the structure like roofs, walls,
              bathrooms, basements, and foundations.
              <br /><br />
              In simple words, waterproofing keeps your home dry, strong, and
              safe by stopping water before it causes damage.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            className="bg-gray-50 rounded-3xl p-10 shadow-xl"
          >
            <h3 className="text-xl font-semibold mb-4">
              Why It Is Essential?
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li>• Prevents seepage & leakage</li>
              <li>• Stops wall dampness</li>
              <li>• Protects concrete & steel</li>
              <li>• Improves indoor air quality</li>
              <li>• Reduces repair cost</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* AREAS */}
      <section className="bg-[#f7f7f7] py-24">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            className="text-4xl font-serif font-bold mb-12"
          >
            Areas That Need Waterproofing
          </motion.h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { icon: Droplets, title: "Bathrooms & Kitchens" },
              { icon: Home, title: "Terrace & Roof" },
              { icon: Building2, title: "External Walls" },
              { icon: Layers, title: "Basements" },
              { icon: Building2, title: "Balconies" },
              { icon: Droplets, title: "Water Tanks" },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <item.icon className="w-12 h-12 text-amber-600 mb-4 mx-auto" />
                <h4 className="text-lg font-semibold">
                  {item.title}
                </h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SIGNS */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-14">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show">
            <h2 className="text-3xl font-semibold mb-4">
              Signs Your Home Needs Waterproofing
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex gap-2"><AlertTriangle /> Peeling or bubbling paint</li>
              <li className="flex gap-2"><AlertTriangle /> Damp wall patches</li>
              <li className="flex gap-2"><AlertTriangle /> Ceiling stains</li>
              <li className="flex gap-2"><AlertTriangle /> White salt marks</li>
              <li className="flex gap-2"><AlertTriangle /> Musty smell</li>
            </ul>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="show">
            <h2 className="text-3xl font-semibold mb-4">
              Long-Term Benefits
            </h2>
            <ul className="space-y-3 text-gray-600">
              <li className="flex gap-2"><CheckCircle /> Higher property value</li>
              <li className="flex gap-2"><CheckCircle /> Structural durability</li>
              <li className="flex gap-2"><CheckCircle /> Better indoor health</li>
              <li className="flex gap-2"><CheckCircle /> Energy efficiency</li>
              <li className="flex gap-2"><CheckCircle /> Low maintenance cost</li>
            </ul>
          </motion.div>
        </div>
      </section>

      
    </div>
  );
};

export default WhatIsWaterproofing;
